import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Asset, Payout, PayoutPostRequest, SPV } from 'src/models';
import { calcEmittedBricks, calcRemainingRentFromLastPayment } from 'src/util/utils';
import { Card, CardContent, Divider, Paper, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { ExecutionDate } from './forms/ExecutionDate';
import { RentSurplus } from './forms/RentSurplus';
import { checkIfCapitalGainsTaxesApply, TaxSystem } from './TaxSystem';

const getTotalBricks = (asset: Asset) => {
  return asset?.investmentDetails?.totalBricks ?? 0;
};

type Props = {
  asset: Asset;
  payouts: Payout[];
  spv: SPV;
  formikArray: any;
  payoutToDispatch: PayoutPostRequest;
  setPayoutToDispatch: React.Dispatch<React.SetStateAction<PayoutPostRequest>>;
};

export const PayoutSystem: FC<Props> = ({ asset, payouts, spv, formikArray, payoutToDispatch, setPayoutToDispatch }) => {
  const { t } = useTranslation();
  const [rentSurplusString, setRentSurplusString] = useState('0.00');
  const [formikExecutionDate, formikEarningPeriod, formikRentSurplus] = formikArray;
  const [issuerTaxTotal, setIssuerTaxTotal] = useState<number>(0);

  const rentPerShareLong = (asset: Asset, rentSurplusString: string, payout: Payout[]) => {
    const rentSurplus = Number(rentSurplusString) ?? 0;
    const remainingRentFromLastPayment = calcRemainingRentFromLastPayment(payout, false) as number;
    const rentPlusRemainingRent = rentSurplus + remainingRentFromLastPayment;
    const totalBricks = getTotalBricks(asset);
    const rentPerShare = totalBricks === 0 ? 0 : rentPlusRemainingRent / totalBricks;
    return rentPerShare;
  };

  const rentPerShareFloored = (asset: Asset, rentSurplusString: string, payout: Payout[], string: boolean): number | string => {
    const rentSurplus = Number(rentSurplusString) ?? 0;
    const remainingRentFromLastPayment = calcRemainingRentFromLastPayment(payout, false) as number;
    const rentPlusRemainingRent = rentSurplus + remainingRentFromLastPayment;
    const totalBricks = getTotalBricks(asset);
    const rentPerShare = totalBricks === 0 ? 0 : rentPlusRemainingRent / totalBricks;
    const rentPerShareFloored = Math.floor(rentPerShare * 100) / 100;
    if (string) {
      return rentPerShareFloored.toFixed(2);
    }
    return rentPerShareFloored;
  };

  const renderTotalGrossRent = (asset: Asset, totalRent: string, color: string, bold: number) => {
    const totalGrossRent = calcTotalGrossRent(asset, rentSurplusString, true) as string;
    useEffect(() => {
      const payoutPost = payoutToDispatch;
      payoutPost.rentalAmount = totalGrossRent;
      setPayoutToDispatch(payoutPost);
    }, [totalGrossRent]);
    return (
      <Grid container>
        <Grid xs={7} sm={5} sx={{ mr: 1 }} display="flex" alignItems="center">
          <Typography variant="body1" color={color} sx={{ fontWeight: bold }}>
            {totalRent}
          </Typography>
        </Grid>
        <Grid data-testid="output-total-gross">€ {totalGrossRent}</Grid>
      </Grid>
    );
  };

  const renderTotalReservedAmount = (asset: Asset, tr: string, color: string, bold: number) => {
    const totalGrossRent = calcTotalGrossRent(asset, rentSurplusString, true) as string;
    useEffect(() => {
      const payoutPost = payoutToDispatch;
      payoutPost.rentalAmount = totalGrossRent;
      setPayoutToDispatch(payoutPost);
    }, [totalGrossRent]);
    return (
      checkIfCapitalGainsTaxesApply(asset.investmentDetails.securityType) && (
        <Grid container>
          <Grid xs={7} sm={5} sx={{ mr: 1 }} display="flex" alignItems="center">
            <Typography variant="body1" color={color} sx={{ fontWeight: bold }}>
              {tr}
            </Typography>
          </Grid>
          <Grid>€ {totalGrossRent}</Grid>
        </Grid>
      )
    );
  };

  const calcTotalGrossRent = (asset: Asset, rentSurplusString: string, string: boolean): number | string => {
    const emittedBricks = calcEmittedBricks(asset);
    const rpsFloored = rentPerShareFloored(asset, rentSurplusString, payouts, false) as number;
    const totalGrossRent = (rpsFloored * emittedBricks).toFixed(2);
    if (string) {
      return totalGrossRent;
    }
    return Number(totalGrossRent);
  };

  const renderRentSurplus = () => {
    return (
      <Grid container>
        <Grid xs={7} sm={5} sx={{ mr: 1 }} display="flex" alignItems="center">
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {t('issuer.rent.form.rentSurplus')}
          </Typography>
        </Grid>
        <Grid>
          <RentSurplus formik={formikRentSurplus} setRentSurplusString={setRentSurplusString} />
        </Grid>
      </Grid>
    );
  };

  const renderRentFromLastPayment = (payout: Payout[]) => {
    const rentFromLastPayment = calcRemainingRentFromLastPayment(payout, true) as string;
    useEffect(() => {
      const payoutPost = payoutToDispatch;
      payoutPost.rentFromLastPayment = rentFromLastPayment;
      setPayoutToDispatch(payoutPost);
    }, [rentFromLastPayment]);
    return (
      <Grid container>
        <Grid xs={7} sm={5} sx={{ mr: 1 }} display="flex" alignItems="center">
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            + {t('issuer.rent.form.rentFromLastPayment')}
          </Typography>
        </Grid>
        <Grid data-testid="output-rent-from-last-payment">€ {rentFromLastPayment}</Grid>
      </Grid>
    );
  };

  const renderTotalRentalAmount = (rentSurplus: string, rentFromLastPayment: number) => {
    const sumRentSurplus = (Number(rentSurplus) + rentFromLastPayment).toFixed(2);
    return (
      <Grid container>
        <Grid xs={7} sm={5} sx={{ mr: 1 }} display="flex" alignItems="center">
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {t('issuer.rent.form.totalRentSurplus')}
          </Typography>
        </Grid>
        <Grid data-testid="output-total-rent-surplus">€ {sumRentSurplus}</Grid>
      </Grid>
    );
  };

  const renderEmittedShares = (asset: Asset) => {
    const emmitedBricks = (asset?.investmentDetails?.totalBricks ?? 0) - (asset.investmentDetails.availableBricks ?? 0);
    return (
      <Grid container>
        <Grid xs={7} sm={5} sx={{ mr: 1 }} display="flex" alignItems="center">
          <Typography variant="body1" color="gray">
            {t('issuer.rent.form.emittedShares')}
          </Typography>
        </Grid>
        <Grid data-testid="output-emitted-shares">
          {emmitedBricks}/{asset.investmentDetails.totalBricks}
        </Grid>
      </Grid>
    );
  };

  const renderRentPerShare = (asset: Asset) => {
    const rpsLong = rentPerShareLong(asset, rentSurplusString, payouts);
    const rpsFloored = rentPerShareFloored(asset, rentSurplusString, payouts, true) as string;
    useEffect(() => {
      const payoutPost = payoutToDispatch;
      payoutPost.rentPerShareRounded = rpsFloored;
      setPayoutToDispatch(payoutPost);
    }, [rpsFloored]);
    return (
      <Grid container>
        <Grid xs={7} sm={5} sx={{ mr: 1 }} display="flex" alignItems="center">
          <Typography variant="body1" color="gray">
            {t('issuer.rent.form.rentPerShare')}
          </Typography>
        </Grid>
        <Grid>
          <span data-testid="output-rent-per-share-floored">€ {rpsFloored}</span>{' '}
          <span data-testid="output-rent-per-share-long"> (€ {rpsLong})</span>
        </Grid>
      </Grid>
    );
  };

  const renderRemainingRentStayingOnAccount = (asset: Asset) => {
    const emittedBricks = calcEmittedBricks(asset);
    const rpsFloored = rentPerShareFloored(asset, rentSurplusString, payouts, false) as number;
    const rpsRaw = rentPerShareLong(asset, rentSurplusString, payouts);
    const remainingRentStayingOnAccount = (rpsRaw * emittedBricks - rpsFloored * emittedBricks).toFixed(2);
    useEffect(() => {
      const payoutPost = payoutToDispatch;
      payoutPost.retainedForNextMonth = remainingRentStayingOnAccount;
      setPayoutToDispatch(payoutPost);
    }, [remainingRentStayingOnAccount]);
    return (
      <Grid container>
        <Grid xs={7} sm={5} sx={{ mr: 1 }} display="flex" alignItems="center">
          <Typography variant="body1" color="gray">
            {t('issuer.rent.form.remaining')}
          </Typography>
        </Grid>
        <Grid data-testid="output-remaining-rent">€ {remainingRentStayingOnAccount}</Grid>
      </Grid>
    );
  };

  const renderTotalNet = (asset: Asset, taxes?: number) => {
    const emittedBricks = calcEmittedBricks(asset);
    const rpsFloored = rentPerShareFloored(asset, rentSurplusString, payouts, false) as number;
    let totalNetRent: string;
    if (taxes) {
      totalNetRent = (rpsFloored * emittedBricks - taxes).toFixed(2);
    } else {
      totalNetRent = (rpsFloored * emittedBricks).toFixed(2);
    }
    return (
      <Grid container>
        <Grid xs={7} sm={5} sx={{ mr: 1 }} display="flex" alignItems="center">
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {t('issuer.rent.form.totalNet')}
          </Typography>
        </Grid>
        <Grid>€ {totalNetRent}</Grid>
      </Grid>
    );
  };

  return (
    <div>
      <Card>
        <CardContent>
          <Typography variant="h5" sx={{ fontWeight: 500, mb: 3 }}>
            {t('issuer.rent.rentPayment')}: {asset.id}
          </Typography>
          <Grid container>
            <Grid xs={7} sm={5} sx={{ mr: 1 }}>
              <Typography variant="body1">{t('issuer.rent.form.subject')}</Typography>
              <Paper sx={{ mb: 3, px: 2, py: 2, my: 2, backgroundColor: '#e5e7eb', display: 'inline-flex' }}>
                <>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {asset.descriptions.title}
                  </Typography>
                  <Typography variant="body1" sx={{ ml: 1 }}>
                    - {asset.id}
                  </Typography>
                </>
              </Paper>
            </Grid>
            <Grid>
              <Typography variant="body1">{t('issuer.rent.executionDate')}</Typography>
              <ExecutionDate formik={formikExecutionDate} />
            </Grid>
          </Grid>
          <Typography variant="subtitle2" color="gray">
            {t('issuer.rent.payoutDisclaimer')}
          </Typography>
          {renderRentSurplus()}
          {renderRentFromLastPayment(payouts)}
          <Divider sx={{ my: 3 }} />
          {renderTotalRentalAmount(rentSurplusString, calcRemainingRentFromLastPayment(payouts, false) as number)}
          {renderEmittedShares(asset)}
          {renderRentPerShare(asset)}
          <Divider sx={{ my: 3 }} />
          {renderRemainingRentStayingOnAccount(asset)}
          {renderTotalGrossRent(asset, t('issuer.rent.form.totalGross'), 'black', 500)}
          {spv && (
            <TaxSystem
              asset={asset}
              spv={spv}
              totalRent={calcTotalGrossRent(asset, rentSurplusString, false) as number}
              rentPerShareFloored={rentPerShareFloored(asset, rentSurplusString, payouts, false) as number}
              formikEarningPeriod={formikEarningPeriod}
              setIssuerTaxTotal={setIssuerTaxTotal}
            />
          )}
          {checkIfCapitalGainsTaxesApply(asset.investmentDetails.securityType) && renderTotalNet(asset, issuerTaxTotal)}
          {renderTotalReservedAmount(asset, t('issuer.rent.form.totalGross.blockedAmount'), 'black', 500)}
        </CardContent>
      </Card>
    </div>
  );
};
